<script setup lang="ts">
/** Webhooks: outbound subscriptions (yours; all for admins), inbound endpoints (admins) and the delivery logs. */
import { ChevronRight, EllipsisVertical, KeyRound, Pencil, Plus, RefreshCw, Send, Trash2, Webhook } from "@lucide/vue";
import { useQueryClient } from "@tanstack/vue-query";
import { computed, ref } from "vue";
import { toast } from "vue-sonner";
import {
  createEndpoint,
  createSubscription,
  deleteEndpoint,
  deleteSubscription,
  redeliver,
  rotateEndpointSecret,
  rotateSubscriptionSecret,
  testSubscription,
  updateEndpoint,
  updateSubscription,
  useDeliveries,
  useEndpoints,
  useInboundDeliveries,
  useSubscriptions,
  useWebhooksInfo,
  type EndpointInput,
  type SubscriptionInput,
  type WebhookEndpoint,
  type WebhookSubscription,
} from "@/api/webhooks";
import CodeBlock from "@/components/common/CodeBlock.vue";
import CopyField from "@/components/common/CopyField.vue";
import EmptyState from "@/components/common/EmptyState.vue";
import ErrorState from "@/components/common/ErrorState.vue";
import FilterBar from "@/components/common/FilterBar.vue";
import JsonView from "@/components/common/JsonView.vue";
import ListSkeleton from "@/components/common/ListSkeleton.vue";
import PageHeader from "@/components/common/PageHeader.vue";
import PageTabs from "@/components/common/PageTabs.vue";
import RelativeTime from "@/components/common/RelativeTime.vue";
import SimpleSelect from "@/components/common/SimpleSelect.vue";
import StatusBadge from "@/components/common/StatusBadge.vue";
import EndpointForm from "@/components/webhooks/EndpointForm.vue";
import SubscriptionForm from "@/components/webhooks/SubscriptionForm.vue";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Switch } from "@/components/ui/switch";
import { confirm } from "@/composables/useConfirm";
import { useRouteQuery } from "@/composables/useRouteQuery";
import { t } from "@/copy/strings";
import { webhooksCopy as c } from "@/copy/webhooks";
import { errorMessage } from "@/lib/errors";
import { cn } from "@/lib/utils";

definePage({ meta: { title: "Webhooks", requires: { capability: "webhooks.write" } } });

const queryClient = useQueryClient();
const info = useWebhooksInfo();
const isAdmin = computed(() => info.data.value?.viewer.isAdmin ?? false);
const tab = useRouteQuery("tab", "outbound");
const subscriptions = useSubscriptions(isAdmin);
const endpoints = useEndpoints(isAdmin);
const tabs = computed(() => [
  { id: "outbound", label: c.tabs.outbound, badge: subscriptions.data.value?.length },
  ...(isAdmin.value ? [{ id: "inbound", label: c.tabs.inbound, badge: endpoints.data.value?.length }] : []),
  { id: "deliveries", label: c.tabs.deliveries },
]);
const refresh = () => queryClient.invalidateQueries({ queryKey: ["webhooks"] });

// Delivery logs
const direction = useRouteQuery("direction", "outbound");
const subscriptionFilter = useRouteQuery("subscription", "");
const endpointFilter = useRouteQuery("endpoint", "");
const statusFilter = useRouteQuery("status", "");
const deliveries = useDeliveries(() => ({ subscriptionId: subscriptionFilter.value || undefined, status: statusFilter.value || undefined }));
const inbound = useInboundDeliveries(() => ({ endpointId: endpointFilter.value || undefined, status: statusFilter.value || undefined }), () => isAdmin.value && direction.value === "inbound");
const expanded = ref<string | null>(null);

const OUT_STATUS: Record<string, { status: string; label: string }> = {
  pending: { status: "pending", label: "Pending" },
  delivered: { status: "succeeded", label: "Delivered" },
  failed: { status: "queued", label: "Retrying" },
  dead: { status: "failed", label: "Given up" },
};
const IN_STATUS: Record<string, { status: string; label: string }> = {
  accepted: { status: "working", label: "Processing" },
  processed: { status: "completed", label: "Processed" },
  ignored: { status: "skipped", label: "Ignored" },
  rejected: { status: "denied", label: "Rejected" },
  failed: { status: "failed", label: "Failed" },
};

// Secrets are shown once, right after creating or rotating.
const secret = ref<{ title: string; secret: string; url?: string; setup?: string[] } | null>(null);
const secretOpen = computed({ get: () => Boolean(secret.value), set: (v) => !v && (secret.value = null) });

// --- subscriptions
const subFormOpen = ref(false);
const editingSub = ref<WebhookSubscription | null>(null);
const busy = ref(false);
const formError = ref<unknown>(null);

function openSub(s: WebhookSubscription | null) {
  editingSub.value = s;
  formError.value = null;
  subFormOpen.value = true;
}

async function saveSub(value: SubscriptionInput) {
  busy.value = true;
  formError.value = null;
  try {
    if (editingSub.value) {
      await updateSubscription(editingSub.value.id, value);
      toast.success(`${value.name} saved.`);
    } else {
      const out = await createSubscription(value);
      secret.value = { title: out.subscription.name, secret: out.secret };
    }
    subFormOpen.value = false;
    await refresh();
  } catch (err) {
    formError.value = err;
  } finally {
    busy.value = false;
  }
}

async function toggleSub(s: WebhookSubscription, enabled: boolean) {
  try {
    await updateSubscription(s.id, { name: s.name, events: s.events, enabled });
    await refresh();
  } catch (err) {
    toast.error(errorMessage(err));
  }
}

const testing = ref<string | null>(null);
async function test(s: WebhookSubscription) {
  testing.value = s.id;
  try {
    const r = await testSubscription(s.id);
    if (r.status === "delivered") toast.success(c.outbound.testOk(r.responseStatus, r.durationMs));
    else toast.error(c.outbound.testFailed(r.error));
    await refresh();
  } catch (err) {
    toast.error(errorMessage(err));
  } finally {
    testing.value = null;
  }
}

async function rotateSub(s: WebhookSubscription) {
  if (!(await confirm({ title: `${c.outbound.rotate.replace("…", "")} for ${s.name}?`, description: c.outbound.rotateConfirm, confirmLabel: c.outbound.rotate.replace("…", "") }))) return;
  try {
    const out = await rotateSubscriptionSecret(s.id);
    secret.value = { title: s.name, secret: out.secret };
  } catch (err) {
    toast.error(errorMessage(err));
  }
}

async function removeSub(s: WebhookSubscription) {
  if (!(await confirm({ title: `${t.common.delete} ${s.name}?`, description: c.outbound.removeConfirm, confirmLabel: t.common.delete, destructive: true }))) return;
  try {
    await deleteSubscription(s.id);
    toast.success(`${s.name} deleted.`);
    await refresh();
  } catch (err) {
    toast.error(errorMessage(err));
  }
}

function showDeliveries(filter: { subscription?: string; endpoint?: string }) {
  direction.value = filter.endpoint ? "inbound" : "outbound";
  subscriptionFilter.value = filter.subscription ?? "";
  endpointFilter.value = filter.endpoint ?? "";
  tab.value = "deliveries";
}

// --- endpoints
const epFormOpen = ref(false);
const editingEp = ref<WebhookEndpoint | null>(null);

function openEp(e: WebhookEndpoint | null) {
  editingEp.value = e;
  formError.value = null;
  epFormOpen.value = true;
}

async function saveEp(value: EndpointInput) {
  busy.value = true;
  formError.value = null;
  try {
    if (editingEp.value) {
      const { provider: _p, ...patch } = value;
      await updateEndpoint(editingEp.value.id, patch);
      toast.success(`${value.name} saved.`);
    } else {
      const out = await createEndpoint(value);
      secret.value = { title: out.endpoint.name, secret: out.secret, url: out.endpoint.url, setup: out.setup };
    }
    epFormOpen.value = false;
    await refresh();
  } catch (err) {
    formError.value = err;
  } finally {
    busy.value = false;
  }
}

async function toggleEp(e: WebhookEndpoint, enabled: boolean) {
  try {
    await updateEndpoint(e.id, { enabled });
    await refresh();
  } catch (err) {
    toast.error(errorMessage(err));
  }
}

async function rotateEp(e: WebhookEndpoint) {
  if (!(await confirm({ title: `New secret for ${e.name}?`, description: c.inbound.rotateConfirm, confirmLabel: c.outbound.rotate.replace("…", "") }))) return;
  try {
    const out = await rotateEndpointSecret(e.id);
    secret.value = { title: e.name, secret: out.secret, url: out.endpoint.url, setup: out.setup };
  } catch (err) {
    toast.error(errorMessage(err));
  }
}

async function removeEp(e: WebhookEndpoint) {
  if (!(await confirm({ title: `${t.common.delete} ${e.name}?`, description: c.inbound.removeConfirm, confirmLabel: t.common.delete, destructive: true, typeToConfirm: e.name }))) return;
  try {
    await deleteEndpoint(e.id);
    toast.success(`${e.name} deleted.`);
    await refresh();
  } catch (err) {
    toast.error(errorMessage(err));
  }
}

async function resend(id: string) {
  try {
    const r = await redeliver(id);
    if (r.status === "delivered") toast.success(c.outbound.testOk(r.responseStatus, r.durationMs));
    else toast.error(c.outbound.testFailed(r.error));
    await refresh();
  } catch (err) {
    toast.error(errorMessage(err));
  }
}

/** One delivery row (outbound and inbound): expands to the details. */
const ROW =
  "flex w-full items-start gap-2.5 px-3 py-2.5 text-left text-sm transition-colors hover:bg-state-hover focus-visible:outline-offset-[-2px] active:bg-state-press sm:items-center sm:px-4";

const eventTitle = (type: string) => info.data.value?.events.find((e) => e.type === type)?.title ?? type;
</script>

<template>
  <div>
    <PageHeader :title="c.title" :description="c.description">
      <template #actions>
        <Button v-if="tab === 'outbound'" @click="openSub(null)"><Plus /> {{ c.outbound.add }}</Button>
        <Button v-else-if="tab === 'inbound' && isAdmin" @click="openEp(null)"><Plus /> {{ c.inbound.add }}</Button>
      </template>
      <template #tabs>
        <PageTabs v-model="tab" :items="tabs" label="Webhooks" id-prefix="webhooks-tab" />
      </template>
    </PageHeader>

    <!-- Outbound -->
    <template v-if="tab === 'outbound'">
      <ListSkeleton v-if="subscriptions.isPending.value" :rows="3" />
      <ErrorState v-else-if="subscriptions.error.value" :error="subscriptions.error.value" @retry="subscriptions.refetch()" />
      <EmptyState v-else-if="!subscriptions.data.value?.length" :icon="Webhook" :title="c.outbound.empty" :description="c.outbound.emptyHint">
        <Button @click="openSub(null)"><Plus /> {{ c.outbound.add }}</Button>
      </EmptyState>
      <ul v-else class="flex flex-col gap-3">
        <li v-for="s in subscriptions.data.value" :key="s.id" class="surface flex flex-col gap-3 p-4 sm:flex-row sm:items-start sm:gap-4">
          <div :class="cn('min-w-0 flex-1', !s.enabled && 'opacity-75')">
            <p class="flex flex-wrap items-center gap-x-2 gap-y-1">
              <span class="card-title min-w-0 [overflow-wrap:anywhere]">{{ s.name }}</span>
              <Badge variant="muted">{{ s.format }}</Badge>
              <Badge v-if="s.scope === 'global'" variant="info">global</Badge>
            </p>
            <p class="mt-1 truncate font-mono text-xs text-muted-foreground" :title="s.url">{{ s.url }}</p>
            <div class="mt-2 flex flex-wrap gap-1">
              <Badge v-for="e in s.events" :key="e" variant="muted" class="font-mono" :title="eventTitle(e)">{{ e }}</Badge>
            </div>
            <p v-if="s.owner && isAdmin" class="mt-2 text-xs text-muted-foreground">{{ c.outbound.owner }}: {{ s.owner }}</p>
            <p v-if="s.disabledReason" class="mt-2 rounded-md bg-destructive-soft px-3 py-2 text-xs text-destructive">{{ c.outbound.disabledBecause }} {{ s.disabledReason }}</p>
            <p v-else-if="s.consecutiveFailures > 0" class="mt-2 text-xs font-medium text-warning-ink">{{ c.outbound.failures(s.consecutiveFailures) }}</p>
          </div>
          <div class="flex items-center gap-2 border-t pt-3 sm:shrink-0 sm:border-t-0 sm:pt-0">
            <label class="mr-auto flex items-center gap-2 text-sm sm:mr-1">
              <Switch :model-value="s.enabled" :aria-label="`${s.name} ${c.outbound.enabled}`" @update:model-value="(v: boolean) => toggleSub(s, v)" />
              {{ s.enabled ? t.common.enabled : t.common.disabled }}
            </label>
            <Button variant="outline" size="sm" :loading="testing === s.id" @click="test(s)"><Send /> {{ c.outbound.test }}</Button>
            <DropdownMenu>
              <DropdownMenuTrigger as-child>
                <Button variant="ghost" size="icon-sm" :aria-label="`More actions for ${s.name}`"><EllipsisVertical /></Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem @select="showDeliveries({ subscription: s.id })"><RefreshCw /> {{ c.tabs.deliveries }}</DropdownMenuItem>
                <DropdownMenuItem @select="openSub(s)"><Pencil /> {{ t.common.edit }}</DropdownMenuItem>
                <DropdownMenuItem @select="rotateSub(s)"><KeyRound /> {{ c.outbound.rotate }}</DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem class="text-destructive" @select="removeSub(s)"><Trash2 /> {{ t.common.delete }}…</DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </li>
      </ul>

      <details v-if="info.data.value" class="group surface mt-8 overflow-hidden">
        <summary class="flex cursor-pointer list-none items-center gap-2 px-4 py-3 text-sm font-medium transition-colors hover:bg-state-hover [&::-webkit-details-marker]:hidden">
          <ChevronRight class="size-4 text-muted-foreground transition-transform group-open:rotate-90" aria-hidden="true" />
          {{ c.events.title }}
          <span class="text-xs font-normal text-muted-foreground tabular-nums">({{ info.data.value.events.length }})</span>
        </summary>
        <dl class="grid gap-x-6 gap-y-4 border-t px-4 py-4 md:grid-cols-2">
          <div v-for="e in info.data.value.events" :key="e.type" class="grid min-w-0 content-start gap-0.5">
            <dt class="font-mono text-sm [overflow-wrap:anywhere]">{{ e.type }} <span class="text-xs text-muted-foreground">v{{ e.version }}</span></dt>
            <dd class="text-sm text-muted-foreground">{{ e.description }} {{ c.events.audience }} {{ e.audience }}.</dd>
          </div>
        </dl>
      </details>
    </template>

    <!-- Inbound -->
    <template v-else-if="tab === 'inbound' && isAdmin">
      <ListSkeleton v-if="endpoints.isPending.value" :rows="2" />
      <ErrorState v-else-if="endpoints.error.value" :error="endpoints.error.value" @retry="endpoints.refetch()" />
      <EmptyState v-else-if="!endpoints.data.value?.length" :icon="Webhook" :title="c.inbound.empty" :description="c.inbound.emptyHint">
        <Button @click="openEp(null)"><Plus /> {{ c.inbound.add }}</Button>
      </EmptyState>
      <ul v-else class="flex flex-col gap-3">
        <li v-for="e in endpoints.data.value" :key="e.id" class="surface flex flex-col gap-3 p-4">
          <div class="flex flex-col gap-3 sm:flex-row sm:items-start sm:gap-4">
            <div :class="cn('min-w-0 flex-1', !e.enabled && 'opacity-75')">
              <p class="flex flex-wrap items-center gap-x-2 gap-y-1">
                <span class="card-title min-w-0 [overflow-wrap:anywhere]">{{ e.name }}</span>
                <Badge variant="muted">{{ e.provider }}</Badge>
              </p>
              <p class="mt-1 text-sm text-muted-foreground [overflow-wrap:anywhere]">
                {{ e.events.length ? e.events.join(", ") : "all events" }} → {{ e.actions.map((a) => a.type).join(", ") || "log only" }}
              </p>
              <p class="mt-1 text-xs text-muted-foreground">{{ c.inbound.lastReceived }} <RelativeTime :value="e.lastReceivedAt" /></p>
            </div>
            <div class="flex items-center gap-2 border-t pt-3 sm:shrink-0 sm:border-t-0 sm:pt-0">
              <label class="mr-auto flex items-center gap-2 text-sm sm:mr-1">
                <Switch :model-value="e.enabled" :aria-label="`${e.name} ${c.outbound.enabled}`" @update:model-value="(v: boolean) => toggleEp(e, v)" />
                {{ e.enabled ? t.common.enabled : t.common.disabled }}
              </label>
              <DropdownMenu>
                <DropdownMenuTrigger as-child>
                  <Button variant="ghost" size="icon-sm" :aria-label="`More actions for ${e.name}`"><EllipsisVertical /></Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem @select="showDeliveries({ endpoint: e.id })"><RefreshCw /> {{ c.tabs.deliveries }}</DropdownMenuItem>
                  <DropdownMenuItem @select="openEp(e)"><Pencil /> {{ t.common.edit }}</DropdownMenuItem>
                  <DropdownMenuItem @select="rotateEp(e)"><KeyRound /> {{ c.outbound.rotate }}</DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem class="text-destructive" @select="removeEp(e)"><Trash2 /> {{ t.common.delete }}…</DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
          <CopyField :value="e.url" :label="c.secret.url" />
        </li>
      </ul>
    </template>

    <!-- Deliveries -->
    <template v-else-if="tab === 'deliveries'">
      <FilterBar label="Filter deliveries" class="mb-5">
        <SimpleSelect
          v-if="isAdmin"
          :model-value="direction"
          :options="[{ value: 'outbound', label: c.deliveries.outbound }, { value: 'inbound', label: c.deliveries.inbound }]"
          aria-label="Direction"
          class="w-full sm:w-48"
          @update:model-value="(v) => { direction = v ?? 'outbound'; statusFilter = ''; }"
        />
        <SimpleSelect
          v-if="direction === 'outbound'"
          :model-value="subscriptionFilter || undefined"
          :options="(subscriptions.data.value ?? []).map((s) => ({ value: s.id, label: s.name }))"
          allow-empty
          :empty-label="c.deliveries.all"
          aria-label="Webhook"
          class="w-full sm:w-60"
          @update:model-value="(v) => (subscriptionFilter = v ?? '')"
        />
        <SimpleSelect
          v-else
          :model-value="endpointFilter || undefined"
          :options="(endpoints.data.value ?? []).map((e) => ({ value: e.id, label: e.name }))"
          allow-empty
          :empty-label="c.deliveries.all"
          aria-label="Endpoint"
          class="w-full sm:w-60"
          @update:model-value="(v) => (endpointFilter = v ?? '')"
        />
        <SimpleSelect
          :model-value="statusFilter || undefined"
          :options="Object.entries(direction === 'outbound' ? OUT_STATUS : IN_STATUS).map(([value, s]) => ({ value, label: s.label }))"
          allow-empty
          :empty-label="t.common.all"
          aria-label="Status"
          class="w-full sm:w-48"
          @update:model-value="(v) => (statusFilter = v ?? '')"
        />
      </FilterBar>

      <template v-if="direction === 'outbound'">
        <ListSkeleton v-if="deliveries.isPending.value" :rows="4" />
        <ErrorState v-else-if="deliveries.error.value" :error="deliveries.error.value" @retry="deliveries.refetch()" />
        <EmptyState v-else-if="!deliveries.data.value?.length" :icon="Send" :title="c.deliveries.empty" compact />
        <ul v-else class="surface divide-y overflow-hidden">
          <li v-for="d in deliveries.data.value" :key="d.id">
            <button
              type="button"
              :class="ROW"
              :aria-expanded="expanded === d.id"
              @click="expanded = expanded === d.id ? null : d.id"
            >
              <ChevronRight :class="cn('mt-0.5 size-4 shrink-0 text-muted-foreground transition-transform sm:mt-0', expanded === d.id && 'rotate-90')" aria-hidden="true" />
              <span class="flex min-w-0 flex-1 flex-col gap-1 sm:flex-row sm:items-center sm:gap-3">
                <span class="flex min-w-0 flex-wrap items-center gap-x-2.5 gap-y-1">
                  <StatusBadge :status="OUT_STATUS[d.status].status" :label="OUT_STATUS[d.status].label" />
                  <span class="min-w-0 font-mono [overflow-wrap:anywhere]">{{ d.eventType }}</span>
                  <span class="min-w-0 text-muted-foreground [overflow-wrap:anywhere]">→ {{ d.subscription }}</span>
                </span>
                <span class="flex flex-wrap gap-x-2 text-xs text-muted-foreground sm:ml-auto sm:shrink-0">
                  <span v-if="d.responseStatus" class="tabular-nums">HTTP {{ d.responseStatus }} ·</span>
                  <span>{{ c.deliveries.attempts(d.attempts) }} ·</span>
                  <RelativeTime :value="d.createdAt" />
                </span>
              </span>
            </button>
            <div v-if="expanded === d.id" class="flex flex-col gap-3 border-t bg-surface-sunken/60 px-3 py-3 sm:px-4 sm:pl-10">
              <p v-if="d.error" class="rounded-md bg-destructive-soft px-3 py-2 text-xs text-destructive">{{ d.error }}</p>
              <p v-if="d.nextAttemptAt" class="text-xs text-muted-foreground">{{ c.deliveries.nextAttempt }} <RelativeTime :value="d.nextAttemptAt" /></p>
              <CodeBlock v-if="d.responseExcerpt" :code="d.responseExcerpt" :title="c.deliveries.response" wrap max-height="8rem" class="bg-card" />
              <JsonView :value="d.data" :max-height="200" :label="c.deliveries.payload" class="bg-card" />
              <Button variant="outline" size="sm" class="self-start" @click="resend(d.id)"><RefreshCw /> {{ c.deliveries.redeliver }}</Button>
            </div>
          </li>
        </ul>
      </template>
      <template v-else>
        <ListSkeleton v-if="inbound.isPending.value" :rows="4" />
        <ErrorState v-else-if="inbound.error.value" :error="inbound.error.value" @retry="inbound.refetch()" />
        <EmptyState v-else-if="!inbound.data.value?.length" :icon="Webhook" :title="c.deliveries.empty" compact />
        <ul v-else class="surface divide-y overflow-hidden">
          <li v-for="d in inbound.data.value" :key="d.id">
            <button
              type="button"
              :class="ROW"
              :aria-expanded="expanded === d.id"
              @click="expanded = expanded === d.id ? null : d.id"
            >
              <ChevronRight :class="cn('mt-0.5 size-4 shrink-0 text-muted-foreground transition-transform sm:mt-0', expanded === d.id && 'rotate-90')" aria-hidden="true" />
              <span class="flex min-w-0 flex-1 flex-col gap-1 sm:flex-row sm:items-center sm:gap-3">
                <span class="flex min-w-0 flex-wrap items-center gap-x-2.5 gap-y-1">
                  <StatusBadge :status="IN_STATUS[d.status]?.status ?? d.status" :label="IN_STATUS[d.status]?.label" />
                  <span class="min-w-0 font-mono [overflow-wrap:anywhere]">{{ d.event }}</span>
                  <span class="min-w-0 text-muted-foreground [overflow-wrap:anywhere]">← {{ d.endpoint }}</span>
                </span>
                <span class="flex flex-wrap gap-x-2 text-xs text-muted-foreground sm:ml-auto sm:shrink-0">
                  <span v-if="d.reason">{{ d.reason }} ·</span>
                  <RelativeTime :value="d.receivedAt" />
                </span>
              </span>
            </button>
            <div v-if="expanded === d.id" class="flex flex-col gap-3 border-t bg-surface-sunken/60 px-3 py-3 sm:px-4 sm:pl-10">
              <ul v-if="d.actions?.length" class="flex flex-col gap-1 text-xs">
                <li v-for="(a, i) in d.actions" :key="i" :class="a.ok ? 'text-muted-foreground' : 'text-destructive'"><span class="font-mono">{{ a.type }}</span>: {{ a.detail }}</li>
              </ul>
              <p v-if="d.document" class="text-xs">Document <span class="font-mono">{{ d.document }}</span></p>
              <RouterLink v-if="d.jobId" :to="`/jobs/${d.jobId}`" class="self-start text-xs font-medium text-primary hover:underline">Job</RouterLink>
              <JsonView v-if="d.payloadExcerpt" :value="d.payloadExcerpt" :max-height="200" :label="c.deliveries.payload" class="bg-card" />
            </div>
          </li>
        </ul>
      </template>
    </template>

    <!-- Forms: long, so sheets with the Save bar pinned to the bottom -->
    <Sheet v-model:open="subFormOpen">
      <SheetContent side="right" class="w-full gap-0 overflow-y-auto sm:max-w-xl">
        <SheetHeader>
          <SheetTitle class="pr-8">{{ editingSub ? c.outbound.edit(editingSub.name) : c.outbound.create }}</SheetTitle>
          <SheetDescription>{{ c.outbound.formHint }}</SheetDescription>
        </SheetHeader>
        <div class="flex flex-1 flex-col px-4">
          <SubscriptionForm
            v-if="subFormOpen && info.data.value"
            :key="editingSub?.id ?? 'new'"
            :subscription="editingSub"
            :events="info.data.value.events"
            :is-admin="isAdmin"
            :busy="busy"
            :error="formError"
            @submit="saveSub"
            @cancel="subFormOpen = false"
          />
        </div>
      </SheetContent>
    </Sheet>

    <Sheet v-model:open="epFormOpen">
      <SheetContent side="right" class="w-full gap-0 overflow-y-auto sm:max-w-xl">
        <SheetHeader>
          <SheetTitle class="pr-8">{{ editingEp ? c.outbound.edit(editingEp.name) : c.inbound.create }}</SheetTitle>
          <SheetDescription>{{ c.inbound.formHint }}</SheetDescription>
        </SheetHeader>
        <div class="flex flex-1 flex-col px-4">
          <EndpointForm v-if="epFormOpen" :key="editingEp?.id ?? 'new'" :endpoint="editingEp" :busy="busy" :error="formError" @submit="saveEp" @cancel="epFormOpen = false" />
        </div>
      </SheetContent>
    </Sheet>

    <Dialog v-model:open="secretOpen">
      <DialogContent class="sm:max-w-xl">
        <DialogHeader>
          <DialogTitle>{{ c.secret.title }}: {{ secret?.title }}</DialogTitle>
          <DialogDescription>{{ c.secret.description }}</DialogDescription>
        </DialogHeader>
        <div v-if="secret" class="flex flex-col gap-3">
          <CopyField :value="secret.secret" label="secret" />
          <template v-if="secret.url">
            <p class="text-sm font-medium">{{ c.secret.url }}</p>
            <CopyField :value="secret.url" :label="c.secret.url" />
          </template>
          <template v-if="secret.setup?.length">
            <p class="text-sm font-medium">{{ c.secret.setup }}</p>
            <ol class="list-decimal space-y-1 pl-5 text-sm text-muted-foreground">
              <li v-for="(step, i) in secret.setup" :key="i">{{ step }}</li>
            </ol>
          </template>
        </div>
        <DialogFooter>
          <Button @click="secret = null">{{ t.common.close }}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  </div>
</template>
